import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import HeroCanvas from "@/components/HeroCanvas";
import content from "@/content/siteContent";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.25 + i * 0.14, duration: 1.0, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { hero } = content;
  return (
    <section data-testid="hero-section" className="hero-glow relative flex min-h-screen items-center overflow-hidden">
      <HeroCanvas />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-36 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-plz-orange"
        >
          {hero.eyebrow}
        </motion.p>
        <h1 className="max-w-4xl font-heading text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-6xl lg:text-7xl">
          {hero.titleLines.map((line, i) => (
            <span key={line.text} className="mask-line">
              <motion.span
                className={`block ${line.accent ? "text-plz-orange" : ""}`}
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base leading-relaxed text-plz-slate sm:text-lg"
        >
          {hero.subheadline}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-portfolio"
            data-scroll-to={hero.primaryCta.target}
            className="group flex items-center gap-2 rounded-full bg-plz-orange px-7 py-3.5 font-heading text-sm font-bold text-plz-cream transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-plz-ember hover:shadow-[0_12px_40px_rgba(65,105,225,0.35)]"
          >
            {hero.primaryCta.label}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </button>
          <button
            data-testid="hero-cta-services"
            data-scroll-to={hero.secondaryCta.target}
            className="rounded-full border border-plz-line px-7 py-3.5 font-heading text-sm font-bold text-plz-cream transition-[background-color,border-color] duration-300 hover:border-plz-orange/60 hover:bg-plz-surface/60"
          >
            {hero.secondaryCta.label}
          </button>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-plz-muted" aria-hidden="true" />
      </motion.div>
    </section>
  );
}
